/**
 * Polling Service
 *
 * Handles asynchronous polling of document processing status with exponential backoff.
 * Manages multiple concurrent polling sessions and enforces timeout limits.
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.7
 */

import type { ApiClient, ApiError } from './apiClient';
import type { ProcessedDocument } from '../types/documents';
import type { ProcessingStatus } from '../types/extraction';

// ============================================================================
// Types and Interfaces
// ============================================================================

/**
 * Configuration for polling behavior.
 *
 * @property initialInterval - Starting interval in milliseconds (e.g., 1000)
 * @property maxInterval - Maximum interval in milliseconds (e.g., 10000)
 * @property backoffMultiplier - Multiplier for exponential backoff (e.g., 1.5)
 * @property maxDuration - Maximum total polling time in milliseconds (e.g., 300000 = 5 minutes)
 */
export interface PollingConfig {
  /** Starting interval in ms (e.g., 1000) */
  initialInterval: number;
  /** Maximum interval in ms (e.g., 10000) */
  maxInterval: number;
  /** Multiplier for exponential backoff (e.g., 1.5) */
  backoffMultiplier: number;
  /** Maximum total polling time in ms (e.g., 300000 = 5 minutes) */
  maxDuration: number;
}

/**
 * Internal state for tracking an active polling session.
 */
interface PollingSession {
  /** Process ID being polled */
  processId: string;
  /** Timestamp when polling started */
  startTime: number;
  /** Current polling interval in ms */
  currentInterval: number;
  /** Timeout ID for the next poll (for cancellation) */
  timeoutId: ReturnType<typeof setTimeout> | null;
  /** Number of poll attempts made */
  attemptCount: number;
  /** Callback for progress updates */
  onProgress: (status: ProcessingStatus) => void;
  /** Callback for successful completion */
  onComplete: (result: ProcessedDocument) => void;
  /** Callback for errors */
  onError: (error: ApiError) => void;
}

/**
 * Interface for the PollingService.
 *
 * Provides methods to start, stop, and check polling status for document processing.
 */
export interface IPollingService {
  /**
   * Start polling for a document's processing status.
   *
   * @param processId - The process ID to poll
   * @param onProgress - Callback invoked with status updates during processing
   * @param onComplete - Callback invoked when processing completes successfully
   * @param onError - Callback invoked when an error occurs
   *
   * Requirement 6.1: Begin polling at regular intervals when document is submitted
   */
  startPolling(
    processId: string,
    onProgress: (status: ProcessingStatus) => void,
    onComplete: (result: ProcessedDocument) => void,
    onError: (error: ApiError) => void
  ): void;

  /**
   * Stop polling for a specific process ID.
   *
   * @param processId - The process ID to stop polling for
   */
  stopPolling(processId: string): void;

  /**
   * Check if polling is active for a specific process ID.
   *
   * @param processId - The process ID to check
   * @returns True if polling is active, false otherwise
   */
  isPolling(processId: string): boolean;
}

// ============================================================================
// Default Configuration
// ============================================================================

/**
 * Default polling configuration values.
 */
export const DEFAULT_POLLING_CONFIG: PollingConfig = {
  initialInterval: 5000,      // 5 seconds
  maxInterval: 5000,          // 5 seconds (fixed interval, no backoff)
  backoffMultiplier: 1,       // No backoff — keep interval constant
  maxDuration: 600000,        // 10 minutes
};

// ============================================================================
// PollingService Implementation
// ============================================================================

/**
 * PollingService class for managing document processing status polling.
 *
 * Features:
 * - Exponential backoff to avoid overwhelming the server (Requirement 6.6)
 * - Timeout enforcement to prevent indefinite polling (Requirement 6.7)
 * - Multiple concurrent polling sessions support
 * - Proper cleanup on stop or completion
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.7
 */
export class PollingService implements IPollingService {
  /** API client for making status requests */
  private apiClient: ApiClient;

  /** Polling configuration */
  private config: PollingConfig;

  /** Map of active polling sessions by process ID */
  private sessions: Map<string, PollingSession>;

  /**
   * Create a new PollingService instance.
   *
   * @param apiClient - The API client to use for status requests
   * @param config - Optional polling configuration (defaults to DEFAULT_POLLING_CONFIG)
   */
  constructor(apiClient: ApiClient, config?: Partial<PollingConfig>) {
    this.apiClient = apiClient;
    this.config = { ...DEFAULT_POLLING_CONFIG, ...config };
    this.sessions = new Map();
  }

  /**
   * Calculate the next polling interval using exponential backoff.
   *
   * Formula: nextInterval = min(currentInterval * backoffMultiplier, maxInterval)
   *
   * @param currentInterval - The current polling interval
   * @returns The next polling interval (capped at maxInterval)
   *
   * Requirement 6.6: Implement exponential backoff
   */
  private calculateNextInterval(currentInterval: number): number {
    const nextInterval = currentInterval * this.config.backoffMultiplier;
    return Math.min(nextInterval, this.config.maxInterval);
  }

  /**
   * Check if the polling session has exceeded the maximum duration.
   *
   * @param session - The polling session to check
   * @returns True if the session has timed out
   *
   * Requirement 6.7: Stop polling and report timeout after maxDuration
   */
  private hasTimedOut(session: PollingSession): boolean {
    const elapsed = Date.now() - session.startTime;
    return elapsed >= this.config.maxDuration;
  }

  /**
   * Execute a single poll for a process ID.
   *
   * This method:
   * 1. Checks for timeout and reports error if exceeded
   * 2. Calls the status endpoint
   * 3. Handles the response based on status code:
   *    - 200 (processing): Report progress and schedule next poll
   *    - 302 (completed): Fetch results and call onComplete
   *    - 404 (not found): Report error
   *    - 500 (server error): Report error
   * 4. Schedules the next poll with exponential backoff
   *
   * @param processId - The process ID to poll
   *
   * Requirements: 6.2, 6.3, 6.4, 6.5, 6.6, 6.7
   */
  private async executePoll(processId: string): Promise<void> {
    const session = this.sessions.get(processId);
    if (!session) {
      // Session was stopped, do nothing
      return;
    }

    // Check for timeout (Requirement 6.7)
    if (this.hasTimedOut(session)) {
      this.stopPolling(processId);
      session.onError({
        type: 'timeout',
        message: 'Processing is taking longer than expected. Please try again later.',
      });
      return;
    }

    try {
      // Call the status endpoint
      const response = await this.apiClient.getContentProcessingStatus(processId);

      // Check if session was stopped during the request
      if (!this.sessions.has(processId)) {
        return;
      }

      if (response.success) {
        const { status, resourceUrl } = response.data;

        switch (status) {
          case 'completed':
            // Requirement 6.3: Stop polling and fetch results when status endpoint returns 302
            await this.handleCompletion(processId, resourceUrl);
            break;

          case 'failed':
            // Processing failed on the server side
            this.stopPolling(processId);
            session.onError({
              type: 'server_error',
              message: 'Document processing failed. Please try again.',
            });
            break;

          default:
            // All other statuses (pending, processing, extract, map, evaluate, save)
            // are in-progress — report the actual status and continue polling
            session.onProgress(status);
            this.scheduleNextPoll(processId);
            break;
        }
      } else {
        // Handle error responses
        const error = response.error;

        switch (error.type) {
          case 'not_found':
            // Requirement 6.4: Display error when status endpoint returns 404
            this.stopPolling(processId);
            session.onError({
              type: 'not_found',
              status: 404,
              message: 'The processing job was not found. It may have been deleted or expired.',
            });
            break;

          case 'server_error':
            // Requirement 6.5: Display error when status endpoint returns 500
            this.stopPolling(processId);
            session.onError({
              type: 'server_error',
              status: error.status,
              message: 'A server error occurred while checking processing status. Please try again later.',
            });
            break;

          case 'network':
            // Network error - retry with backoff
            session.onProgress('processing');
            this.scheduleNextPoll(processId);
            break;

          default:
            // Other errors - stop polling and report
            this.stopPolling(processId);
            session.onError(error);
            break;
        }
      }
    } catch (error) {
      // Unexpected error - stop polling and report
      const session = this.sessions.get(processId);
      if (session) {
        this.stopPolling(processId);
        session.onError({
          type: 'unknown',
          message: error instanceof Error ? error.message : 'An unexpected error occurred',
          originalError: error instanceof Error ? error : undefined,
        });
      }
    }
  }

  /**
   * Handle successful completion of processing.
   *
   * Fetches the processed document results and calls the onComplete callback.
   *
   * @param processId - The process ID that completed
   * @param resourceUrl - Optional resource URL from the status response
   *
   * Requirement 6.3: Stop polling and fetch results when status endpoint returns 302
   */
  private async handleCompletion(processId: string, resourceUrl?: string): Promise<void> {
    const session = this.sessions.get(processId);
    if (!session) {
      return;
    }

    try {
      // Fetch the processed document results
      const resultResponse = await this.apiClient.getProcessedDocument(processId);

      // Stop polling before calling callback
      this.stopPolling(processId);

      if (resultResponse.success) {
        session.onComplete(resultResponse.data);
      } else {
        session.onError({
          type: resultResponse.error.type,
          status: resultResponse.error.status,
          message: 'Processing completed but failed to fetch results. Please refresh the page.',
        });
      }
    } catch (error) {
      this.stopPolling(processId);
      session.onError({
        type: 'unknown',
        message: 'Processing completed but failed to fetch results. Please refresh the page.',
        originalError: error instanceof Error ? error : undefined,
      });
    }
  }

  /**
   * Schedule the next poll with exponential backoff.
   *
   * @param processId - The process ID to schedule polling for
   *
   * Requirement 6.6: Implement exponential backoff
   */
  private scheduleNextPoll(processId: string): void {
    const session = this.sessions.get(processId);
    if (!session) {
      return;
    }

    // Calculate next interval with exponential backoff
    const nextInterval = this.calculateNextInterval(session.currentInterval);

    // Update session state
    session.currentInterval = nextInterval;
    session.attemptCount++;

    // Schedule next poll
    session.timeoutId = setTimeout(() => {
      this.executePoll(processId);
    }, session.currentInterval);
  }

  /**
   * Start polling for a document's processing status.
   *
   * If polling is already active for this process ID, it will be stopped
   * and restarted with fresh state.
   *
   * @param processId - The process ID to poll
   * @param onProgress - Callback invoked with status updates during processing
   * @param onComplete - Callback invoked when processing completes successfully
   * @param onError - Callback invoked when an error occurs
   *
   * Requirement 6.1: Begin polling at regular intervals when document is submitted
   */
  startPolling(
    processId: string,
    onProgress: (status: ProcessingStatus) => void,
    onComplete: (result: ProcessedDocument) => void,
    onError: (error: ApiError) => void
  ): void {
    // Stop any existing polling for this process ID
    if (this.sessions.has(processId)) {
      this.stopPolling(processId);
    }

    // Create new polling session
    const session: PollingSession = {
      processId,
      startTime: Date.now(),
      currentInterval: this.config.initialInterval,
      timeoutId: null,
      attemptCount: 0,
      onProgress,
      onComplete,
      onError,
    };

    // Store session
    this.sessions.set(processId, session);

    // Start polling immediately
    this.executePoll(processId);
  }

  /**
   * Stop polling for a specific process ID.
   *
   * Clears any pending timeout and removes the session from tracking.
   *
   * @param processId - The process ID to stop polling for
   */
  stopPolling(processId: string): void {
    const session = this.sessions.get(processId);
    if (session) {
      // Clear pending timeout
      if (session.timeoutId !== null) {
        clearTimeout(session.timeoutId);
        session.timeoutId = null;
      }

      // Remove session
      this.sessions.delete(processId);
    }
  }

  /**
   * Check if polling is active for a specific process ID.
   *
   * @param processId - The process ID to check
   * @returns True if polling is active, false otherwise
   */
  isPolling(processId: string): boolean {
    return this.sessions.has(processId);
  }

  /**
   * Stop all active polling sessions.
   *
   * Useful for cleanup when the component unmounts or the user navigates away.
   */
  stopAllPolling(): void {
    for (const processId of this.sessions.keys()) {
      this.stopPolling(processId);
    }
  }

  /**
   * Get the current polling configuration.
   *
   * @returns The current PollingConfig
   */
  getConfig(): PollingConfig {
    return { ...this.config };
  }

  /**
   * Get the number of active polling sessions.
   *
   * @returns The number of active sessions
   */
  getActiveSessionCount(): number {
    return this.sessions.size;
  }
}

// ============================================================================
// Factory Functions
// ============================================================================

/**
 * Create a new PollingService instance with the provided API client.
 *
 * @param apiClient - The API client to use for status requests
 * @param config - Optional polling configuration
 * @returns A new PollingService instance
 */
export const createPollingService = (
  apiClient: ApiClient,
  config?: Partial<PollingConfig>
): PollingService => {
  return new PollingService(apiClient, config);
};

/**
 * Calculate the interval for a specific poll attempt using exponential backoff.
 *
 * This is a pure function useful for testing the backoff algorithm.
 *
 * Formula: interval = min(initialInterval * backoffMultiplier^attemptNumber, maxInterval)
 *
 * @param attemptNumber - The poll attempt number (0-indexed)
 * @param config - The polling configuration
 * @returns The interval in milliseconds for this attempt
 *
 * Requirement 6.6: Implement exponential backoff
 */
export const calculateIntervalForAttempt = (
  attemptNumber: number,
  config: PollingConfig = DEFAULT_POLLING_CONFIG
): number => {
  const interval = config.initialInterval * Math.pow(config.backoffMultiplier, attemptNumber);
  return Math.min(interval, config.maxInterval);
};

export default PollingService;
